import React, { memo, MouseEvent as ReactMouseEvent } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Button } from 'common/components';
import { NodeComponentProps, DecisionNodeData, EndNodeData } from './WorkflowBuilder.types';

export const DefaultNode: React.FC<NodeComponentProps> = ({ id, data }) => {
    const handleDeleteClick = (event: ReactMouseEvent<HTMLButtonElement>) => {
        // Stop the event from propagating to the node
        event.stopPropagation();
        // Call the onDelete handler passed through data and pass the event
        if (data.onDelete) {
            data.onDelete(id, event);
        }
    };

    return (
        <div className='workflowNodeBox' style={{ minWidth: '250px', position: 'relative' }}>
            <Button className='deleteNodeButton' onClick={handleDeleteClick}>
                X
            </Button>
            <div className='nodeTitle'>{data.label || 'Node'}</div>
            {data.subLabel && <div className='nodeSubtitle'>{data.subLabel}</div>}
            <Handle type='target' position={Position.Top} />
            <Handle type='source' position={Position.Bottom} />
        </div>
    );
};

export const CallNode: React.FC<NodeComponentProps> = ({ id, data }) => {
    const handleDeleteClick = (event: ReactMouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        if (data.onDelete) {
            data.onDelete(id, event);
        }
    };

    return (
        <div className='workflowNodeBox callNode' style={{ minWidth: '250px', position: 'relative' }}>
            <Button className='deleteNodeButton' onClick={handleDeleteClick}>
                X
            </Button>
            <div className='nodeTitle'>{data.label || 'Call'}</div>
            {data.subLabel && <div className='nodeSubtitle'>{data.subLabel}</div>}
            <Handle type='target' position={Position.Top} />
            <Handle type='source' position={Position.Bottom} />
        </div>
    );
};

export const SMSNode: React.FC<NodeComponentProps> = ({ id, data }) => {
    const handleDeleteClick = (event: ReactMouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        if (data.onDelete) {
            data.onDelete(id, event);
        }
    };

    return (
        <div className='workflowNodeBox smsNode' style={{ minWidth: '250px', position: 'relative' }}>
            <Button className='deleteNodeButton' onClick={handleDeleteClick}>
                X
            </Button>
            <div className='nodeTitle'>{data.label || 'SMS'}</div>
            {data.subLabel && <div className='nodeSubtitle'>{data.subLabel}</div>}
            <Handle type='target' position={Position.Top} />
            <Handle type='source' position={Position.Bottom} />
        </div>
    );
};

export const EmailNode: React.FC<NodeComponentProps> = ({ id, data }) => {
    const handleDeleteClick = (event: ReactMouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        if (data.onDelete) {
            data.onDelete(id, event);
        }
    };

    return (
        <div className='workflowNodeBox emailNode' style={{ minWidth: '250px', position: 'relative' }}>
            <Button className='deleteNodeButton' onClick={handleDeleteClick}>
                X
            </Button>
            <div className='nodeTitle'>{data.label || 'Email'}</div>
            {data.subLabel && <div className='nodeSubtitle'>{data.subLabel}</div>}
            <Handle type='target' position={Position.Top} />
            <Handle type='source' position={Position.Bottom} />
        </div>
    );
};

export const DelayNode: React.FC<NodeComponentProps> = ({ id, data }) => {
    const handleDeleteClick = (event: ReactMouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        if (data.onDelete) {
            data.onDelete(id, event);
        }
    };

    return (
        <div className='workflowNodeBox delayNode' style={{ minWidth: '250px', position: 'relative' }}>
            <Button className='deleteNodeButton' onClick={handleDeleteClick}>
                X
            </Button>
            <div className='nodeTitle'>{data.label || 'Delay'}</div>
            {data.subLabel && <div className='nodeSubtitle'>{data.subLabel}</div>}
            <Handle type='target' position={Position.Top} />
            <Handle type='source' position={Position.Bottom} />
        </div>
    );
};

export const DecisionNode: React.FC<NodeComponentProps> = ({ id, data }) => {
    const handleDeleteClick = (event: ReactMouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        if (data.onDelete) {
            data.onDelete(id, event);
        }
    };
    const decisionData = data as DecisionNodeData;
    const conditions = decisionData.conditions || [];
    const hasDefault = !!decisionData.defaultNextNode;
    const totalHandles = conditions.length + (hasDefault ? 1 : 0);

    return (
        <div className='workflowNodeBox decisionNode' style={{ minWidth: '250px', position: 'relative' }}>
            <Button className='deleteNodeButton' onClick={handleDeleteClick}>
                X
            </Button>
            <div className='nodeTitle'>{data.label || 'Decision'}</div>
            {data.subLabel && (
                <div className='nodeSubtitle' style={{ whiteSpace: 'pre-line' }}>
                    {data.subLabel}
                </div>
            )}
            <Handle type='target' position={Position.Top} />
            {conditions.map((_, index) => (
                <Handle
                    key={`condition-${index}`}
                    type='source'
                    position={Position.Bottom}
                    id={`condition-${index}`}
                    style={{
                        left: totalHandles > 0 ? `${((index + 1) / (totalHandles + 1)) * 100}%` : undefined,
                        transform: 'translateX(-50%)',
                    }}
                />
            ))}
            {hasDefault && (
                <Handle
                    type='source'
                    position={Position.Bottom}
                    id='default'
                    style={{
                        left: totalHandles > 0 ? `${(totalHandles / (totalHandles + 1)) * 100}%` : undefined,
                        transform: 'translateX(-50%)',
                    }}
                />
            )}
        </div>
    );
};

export const EndNode: React.FC<NodeComponentProps> = ({ id, data }) => {
    const handleDeleteClick = (event: ReactMouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        if (data.onDelete) {
            data.onDelete(id, event);
        }
    };

    return (
        <div className='workflowNodeBox endNode' style={{ minWidth: '250px', position: 'relative' }}>
            <Button className='deleteNodeButton' onClick={handleDeleteClick}>
                X
            </Button>
            <div className='nodeTitle'>{data.label || 'End'}</div>
            {data.subLabel && <div className='nodeSubtitle'>{data.subLabel}</div>}
            <Handle type='target' position={Position.Top} />
        </div>
    );
};

/** Synthetic anchor on the main canvas only (edges to terminal decision). No visible UI. */
export const TerminalBridgeNode = memo(function TerminalBridgeNode() {
    return (
        <div
            aria-hidden
            style={{
                width: 0,
                height: 0,
                opacity: 0,
                position: 'relative',
                pointerEvents: 'none',
            }}
        >
            <Handle type='target' position={Position.Top} />
            <Handle type='source' position={Position.Bottom} />
        </div>
    );
});
